/**
 * The inbox screen's controller: `notifications.model.ts` wired to
 * `fetchNotifications`, `markNotificationsSeen` and `markNotificationsRead`.
 *
 * What it owns that the model cannot is the loaded response, the chosen tab,
 * and the optimistic read-marking that runs as the reader leaves the page. The
 * model decides what a row says and where its button goes; the constants decide
 * the words. The badge in the sidebar reads the same endpoint but is not this
 * hook — see the note on "seen" below for why that matters.
 */

import { useCallback, useEffect, useState } from "react";
import type { NotificationsResponse, WorkspaceRef } from "@ondo/shared";

import {
  fetchNotifications,
  markNotificationsRead,
  markNotificationsSeen,
} from "../lib/api.ts";
import {
  describeError,
  emptyFor,
  inTab,
  pending,
  tiles,
  toMarkRead,
  toRow,
} from "./notifications.model.ts";
import type { NotificationRow, Tile } from "./notifications.model.ts";
import type { NotificationTab } from "./notifications.constants.ts";

export type NotificationsView =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "open";
      tab: NotificationTab;
      tiles: Tile[];
      rows: NotificationRow[];
      counts: Record<NotificationTab, number>;
      /** Null when there is something to show. Not a boolean — the copy differs. */
      empty: { lead: string; detail: string } | null;
      pending: string;
      unread: number;
    };

export interface NotificationsController {
  view: NotificationsView;
  chooseTab: (tab: NotificationTab) => void;
  /** Called as a row's action is followed, so following it is what marks it read. */
  open: (id: string) => void;
  markAllRead: () => void;
}

/**
 * `inboxOndoId` is WHOSE inbox, and it is not always the reader's.
 *
 * A gallery or an advisory is told things at the FIRM's ONDO id —
 * `settlement.due` goes to the covenant holder, `consignment.offered` to the
 * gallery it was offered to — and no person's feed carries either. So the open
 * workspace decides which feed to read, exactly as it already decides which
 * verification inbox to open.
 *
 * Null on a personal workspace, which is the reader's own feed and the common
 * case. The server verifies the id against `principals_read` rather than
 * trusting it, so passing one here is addressing, not authorization.
 */
export function useNotificationsController(
  workspace: WorkspaceRef,
  inboxOndoId: string | null = null,
): NotificationsController {
  const [loaded, setLoaded] = useState<NotificationsResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [tab, setTab] = useState<NotificationTab>("all");

  useEffect(() => {
    let active = true;

    fetchNotifications(inboxOndoId)
      .then((response) => {
        if (!active) return;
        setLoaded(response);

        // Seen, not read. Opening the inbox is how you stop something being NEW,
        // and it is a separate call so that the sidebar badge — which reads the
        // same endpoint on every page — cannot clear itself just by rendering.
        // Failing costs a count and nothing else, so it is swallowed.
        void markNotificationsSeen(inboxOndoId).catch(() => undefined);
      })
      .catch((error: unknown) => {
        if (active) setFailure(describeError(error));
      });

    return () => {
      active = false;
    };
  }, [inboxOndoId]);

  /**
   * Marks read locally first, then tells the server.
   *
   * Optimistic on purpose: this fires as the reader follows a link away from
   * the screen, so waiting for the round trip would mean waiting to leave. A
   * failed write leaves a row that reads as read here and unread on the next
   * load, which is the mildest wrong answer available — the row is still there
   * and still says what it said.
   */
  const markRead = useCallback((ids: string[]) => {
    if (ids.length === 0) return;

    setLoaded((current) =>
      current
        ? {
            ...current,
            notifications: current.notifications.map((entry) =>
              ids.includes(entry.id) ? { ...entry, read: true } : entry,
            ),
            unread: Math.max(0, current.unread - ids.length),
          }
        : current,
    );

    void markNotificationsRead({ ids }, inboxOndoId).catch(() => undefined);
  }, [inboxOndoId]);

  const rows = (loaded?.notifications ?? []).map((entry) => toRow(entry, workspace));

  return {
    view: failure
      ? { kind: "error", message: failure }
      : !loaded
        ? { kind: "loading" }
        : resolve(rows, tab, loaded.unread),
    chooseTab: setTab,
    open: (id) => markRead(toMarkRead(rows, id)),
    markAllRead: () => markRead(rows.filter((row) => !row.read).map((row) => row.id)),
  };
}

function resolve(
  rows: NotificationRow[],
  tab: NotificationTab,
  unread: number,
): NotificationsView {
  const shown = inTab(rows, tab);

  return {
    kind: "open",
    tab,
    tiles: tiles(rows, unread),
    rows: shown,
    counts: {
      all: rows.length,
      asks: inTab(rows, "asks").length,
      answers: inTab(rows, "answers").length,
      affiliations: inTab(rows, "affiliations").length,
      record: inTab(rows, "record").length,
    },
    empty: emptyFor({ total: rows.length, shown: shown.length }),
    pending: pending(rows),
    unread,
  };
}
